import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Tooltip,
  TextField,
  InputAdornment,
} from "@mui/material";
import CommonModal from "../../../components/common/CommonModal";
import SearchIcon from "@mui/icons-material/Search";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import SubjectFormModal from "../../../components/Modals/SubjectFormModal";
import { useSubjects, SubjectFormData, Subject } from "../../../hooks/useSubjects";
import { useSubjectMarkMapping, SubjectMarksMapping } from "../../../hooks/useSubjectMarkMapping";
import { useSessionClass } from "contexts/SessionClassContext";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/AuthContext";
import { useSchoolIdForAdmin } from "../../../hooks/useSchoolIdForAdmin";
import EnhancedTable from "../../../components/common/EnhancedTable";
import ActionButtons from "../../../components/common/ActionButtons";
import { useStudents } from "../../../hooks/useStudents";
import { useError } from "../../../contexts/ErrorContext";
import { useApplicationForm } from "../../../hooks/useApplicationForm";
import { getAllMarksheetsByClassIdAndSessionId } from "../../../hooks/useMarkSheets";
const SubjectManagement: React.FC = () => {
  // 1. Move all useState hooks to the top
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentSubject, setCurrentSubject] = useState<SubjectFormData | undefined>(undefined);
  const [currentMappingData, setCurrentMappingData] = useState<SubjectMarksMapping | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [subjectToDelete, setSubjectToDelete] = useState<number | null>(null);
  const [isCreatingBulk, setIsCreatingBulk] = useState(false);
  const [lockSubjectCoreFields, setLockSubjectCoreFields] = useState(false);
  const [lockSubjectAllFields, setLockSubjectAllFields] = useState(false);

  // 2. Get all necessary context and hooks
  const { selectedSession, selectedClass, selectedClassName } = useSessionClass();
  const { t } = useTranslation();
  const { user, isTeacher, isSchoolAdmin } = useAuth();
  const { showError, showSuccess } = useError();
  // Disable API call for school admin and teachers - they already have schoolId from user context
  const { data: schoolIdFromAdmin, isLoading: schoolIdLoading } = useSchoolIdForAdmin(
    isSchoolAdmin() || isTeacher() ? undefined : user?.id
  );
  const queryClient = useQueryClient();
  const SubjectFormModalAny = SubjectFormModal as any;

  // 3. Use useMemo for computed values
  const schoolId = useMemo(() => {
    // For teachers, use their schoolId directly
    if (isTeacher()) {
      return user?.schoolId;
    }

    // For school admins, prioritize their direct schoolId, fallback to admin lookup
    if (user?.schoolId) {
      return user.schoolId;
    }

    // Only use admin lookup if user doesn't have a direct schoolId
    return schoolIdFromAdmin;
  }, [user?.schoolId, schoolIdFromAdmin, isTeacher]);

  // Clear subjects cache when schoolId changes to prevent cross-school data
  useEffect(() => {
    if (schoolId) {
      // Invalidate subjects queries when schoolId changes
      queryClient.invalidateQueries({
        queryKey: ["subjects", schoolId],
      });
    }
  }, [schoolId, queryClient]);

  // 4. Initialize queries
  const {
    subjectsQuery,
    createSubjectMutation,
    updateSubjectMutation,
    deleteSubjectMutation,
  } = useSubjects(false, schoolId);
  const {
    addSubjectMarkMappingMutation,
    deleteSubjectMarkMappingMutation,
    updateSubjectMarkMappingMutation,
  } = useSubjectMarkMapping();

  // Get students for selected session/class to check if subject is used by any student
  const { useStudentsBySessionIdAndClassId } = useStudents(false, schoolId);
  const studentsBySessionClassQuery = useStudentsBySessionIdAndClassId(
    selectedSession?.id,
    selectedClass?.id
  );
  // Marksheets for selected class/session: used to lock edit rules per subject.
  const [marksheetSubjectCodes, setMarksheetSubjectCodes] = useState<Set<string>>(new Set());

  useEffect(() => {
    const classId = selectedClass?.id;
    const sessionId = selectedSession?.id;
    if (!classId || !sessionId) {
      setMarksheetSubjectCodes(new Set());
      return;
    }
    
    let cancelled = false;
    (async () => {
      try {
        const res = await queryClient.fetchQuery({
          queryKey: ["marksheetsAllForSubjectMgmt", classId, sessionId],
          queryFn: () => getAllMarksheetsByClassIdAndSessionId(classId, sessionId, { size: 200, maxPages: 50 }),
          staleTime: 30_000,
        });
        if (cancelled) return;
        const set = new Set<string>();
        (res || []).forEach((ms: any) => {
          (ms?.subjects || []).forEach((s: any) => {
            const code = String(s?.subjectCode || "").trim();
            if (code) set.add(code);
          });
        });
        setMarksheetSubjectCodes(set);
      } catch {
        if (!cancelled) setMarksheetSubjectCodes(new Set());
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [queryClient, selectedClass?.id, selectedSession?.id]);

  // Get application forms data to check if subject is used in any application form
  const { applicationFormsBySchoolIdQuery } = useApplicationForm(false, schoolId);

  // Fetch application forms when schoolId is available to check subject usage
  useEffect(() => {
    if (schoolId) {
      // Fetch application forms to check if subjects are used in application forms
      queryClient.fetchQuery({
        queryKey: ["applicationFormsBySchoolId", schoolId],
        queryFn: async () => {
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/application-forms`);
          return response.data.filter((form: any) => form.schoolId === schoolId);
        },
      });
    }
  }, [schoolId, queryClient]);

  // Force refresh subjects data when component mounts to ensure fresh data after reload
  useEffect(() => {
    if (schoolId && !subjectsQuery.isLoading) {
      // Refetch subjects data to ensure we have the latest data after page reload
      queryClient.refetchQueries({
        queryKey: ["subjects", schoolId],
      });
    }
  }, [schoolId, queryClient, subjectsQuery.isLoading]);

  // 5. Define all callback functions BEFORE any early returns
  const handleOpenAddModal = useCallback(() => {
    setIsEdit(false);
    setIsBulkMode(false);
    setCurrentSubject(undefined);
    setCurrentMappingData(undefined);
    setLockSubjectCoreFields(false);
    setModalOpen(true);
  }, []);

  const handleOpenBulkModal = useCallback(() => {
    setIsEdit(false);
    setIsBulkMode(true);
    setCurrentSubject(undefined);
    setCurrentMappingData(undefined);
    setLockSubjectCoreFields(false);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setIsBulkMode(false);
    setCurrentMappingData(undefined);
  }, []);

  const handleSubmit = useCallback(async (formData: SubjectFormData, mappingData?: SubjectMarksMapping) => {
    // Validate subject code uniqueness within the same class
    if (!formData.subjectCode || !selectedClass) {
      return;
    }

    // Get all subjects for the current class
    const subjectsInSameClass = (subjectsQuery.data || []).filter((subject: Subject) => {
      // Check if subject belongs to the same class
      if (subject.classId === selectedClass.id) {
        return true;
      }

      // Also check by className matching (for backward compatibility)
      const normalizedSubjectClassName = subject.className?.replace(/\s+/g, '').toLowerCase();
      const normalizedSelectedClassName = selectedClass.name?.replace(/\s+/g, '').toLowerCase();
      const normalizedSelectedSection = selectedClass.section?.replace(/\s+/g, '').toLowerCase();

      if (normalizedSubjectClassName === normalizedSelectedClassName) {
        return true;
      }

      // Check if class name matches with section
      if (normalizedSelectedSection && normalizedSubjectClassName?.includes(normalizedSelectedSection)) {
        return true;
      }

      return false;
    });

    // Check for duplicate subject code in the same class
    const duplicateSubject = subjectsInSameClass.find((subject: Subject) => {
      // Skip the current subject if editing
      if (isEdit && currentSubject?.id && subject.id === currentSubject.id) {
        return false;
      }
      return subject.subjectCode?.toLowerCase().trim() === formData.subjectCode?.toLowerCase().trim();
    });

    if (duplicateSubject) {
      showError(t("subject_code_already_exists_in_class"));
      return;
    }

    const payload = {
      ...formData,
      classId: selectedClass?.id || 0,
      className: selectedClassName,
      schoolId: schoolId as number, // Type assertion to fix TypeScript error
    };

    if (isEdit && currentSubject?.id) {
      updateSubjectMutation.mutate(
        { id: currentSubject.id, data: payload },
        {
          onError: (error: unknown) => {
            const err = error as any;
            const backendMessage = err?.response?.data?.message;
            showError(backendMessage || t("error_updating_subject"));
          },
          onSuccess: () => {
            if (!mappingData) {
              showSuccess(t("subject_updated_successfully"));
              handleCloseModal();
              return;
            }

            const getBaseClassName = (name?: string): string => {
              if (!name) return "";
              const match = name.match(/^(\d+(?:th|st|nd|rd)?)/i);
              return match ? match[1] : name.split(/\s+/)[0];
            };

            const baseClassName = getBaseClassName(selectedClassName || currentSubject?.className);

            const mappingPayload: SubjectMarksMapping = {
              ...mappingData,
              className: selectedClassName || "",
              subjectCode: payload.subjectCode,
              subjectName: payload.name,
              subjectType: mappingData.subjectType || payload.subjectCategory || "",
            };

            // Fetch existing mappings (for id) and reuse that id in PUT payload.
            void queryClient
              .fetchQuery({
                queryKey: ["subject-marks-mapping", baseClassName],
                queryFn: async () => {
                  const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/subject-mark-mapping/${baseClassName}`
                  );
                  return response.data as SubjectMarksMapping[];
                },
              })
              .then((mappings) => {
                const oldCode = currentSubject?.subjectCode || payload.subjectCode;
                const existing = mappings?.find((m) => String(m.subjectCode) === String(oldCode));

                updateSubjectMarkMappingMutation.mutate(
                  {
                    ...mappingPayload,
                    id: existing?.id,
                  },
                  {
                    onSuccess: () => {
                      showSuccess(t("subject_updated_successfully"));
                      handleCloseModal();
                    },
                    onError: (error: unknown) => {
                      const err = error as any;
                      const backendMessage = err?.response?.data?.message;
                      showError(backendMessage || "Subject updated but failed to update subject marks mapping.");
                    },
                  }
                );
              })
              .catch((error: unknown) => {
                const err = error as any;
                const backendMessage = err?.response?.data?.message;
                showError(backendMessage || "Failed to load existing mapping id for update.");
              });
          },
        }
      );
    } else {
      if (!mappingData) {
        // Safety fallback: if mapping isn't provided, don't create anything.
        showError("Subject marks mapping is required before creating a subject.");
        return;
      }

      // Create mapping FIRST. Only if it succeeds, create the subject.
      const mappingPayload: SubjectMarksMapping = {
        ...mappingData,
        className: selectedClassName || "",
        subjectCode: payload.subjectCode,
        subjectName: payload.name,
        subjectType: mappingData.subjectType || payload.subjectCategory || "",
      };

      addSubjectMarkMappingMutation.mutate(mappingPayload, {
        onError: (error: unknown) => {
          const err = error as any;
          const backendMessage = err?.response?.data?.message;
          showError(backendMessage || "Failed to create subject marks mapping. Subject was not created.");
        },
        onSuccess: () => {
          createSubjectMutation.mutate(payload, {
            onError: (error: unknown) => {
              const err = error as any;
              const backendMessage = err?.response?.data?.message;
              showError(backendMessage || t("error_creating_subject"));
            },
            onSuccess: () => {
              showSuccess(t("subject_created_successfully"));
              handleCloseModal();
            },
          });
        },
      });
    }
  }, [
    isEdit,
    currentSubject?.id,
    currentSubject?.className,
    currentSubject?.subjectCode,
    selectedClass,
    selectedClassName,
    schoolId,
    subjectsQuery.data,
    updateSubjectMutation,
    createSubjectMutation,
    addSubjectMarkMappingMutation,
    updateSubjectMarkMappingMutation,
    handleCloseModal,
    showError,
    showSuccess,
    queryClient,
    t,
  ]);

  const handleBulkSubmit = useCallback(async (subjects: SubjectFormData[]) => {
    if (!selectedClass) {
      return;
    }

    // Validate all subject codes for duplicates within the same class before submitting
    const subjectsInSameClass = (subjectsQuery.data || []).filter((subject: Subject) => {
      if (subject.classId === selectedClass.id) {
        return true;
      }

      const normalizedSubjectClassName = subject.className?.replace(/\s+/g, '').toLowerCase();
      const normalizedSelectedClassName = selectedClass.name?.replace(/\s+/g, '').toLowerCase();
      const normalizedSelectedSection = selectedClass.section?.replace(/\s+/g, '').toLowerCase();

      if (normalizedSubjectClassName === normalizedSelectedClassName) {
        return true;
      }

      if (normalizedSelectedSection && normalizedSubjectClassName?.includes(normalizedSelectedSection)) {
        return true;
      }

      return false;
    });

    // Check for duplicates in the bulk submission itself
    const subjectCodesInBulk = subjects.map(s => s.subjectCode?.toLowerCase().trim()).filter(Boolean);
    const duplicateInBulk = subjectCodesInBulk.some((code, index) =>
      subjectCodesInBulk.indexOf(code) !== index
    );

    if (duplicateInBulk) {
      showError(t("duplicate_subject_codes_in_bulk_submission"));
      return;
    }

    // Check for duplicates against existing subjects in the same class
    for (const subjectData of subjects) {
      if (!subjectData.subjectCode) continue;

      const duplicateSubject = subjectsInSameClass.find((subject: Subject) => {
        return subject.subjectCode?.toLowerCase().trim() === subjectData.subjectCode?.toLowerCase().trim();
      });

      if (duplicateSubject) {
        showError(
          t("subject_code_already_exists_in_class_with_code", {
            code: subjectData.subjectCode
          })
        );
        return;
      }
    }

    setIsCreatingBulk(true);
    try {
      // Create all subjects sequentially to avoid conflicts
      for (const subjectData of subjects) {
        // Do NOT send mappingData to subject API (different API).
        const { mappingData: mappingFromModal, ...subjectOnly } = subjectData as any;

        const payload = {
          ...subjectOnly,
          classId: selectedClass?.id || 0,
          className: selectedClassName,
          schoolId: schoolId as number,
        };

        // Create/update mapping FIRST. Only if it succeeds, create the subject.
        const mappingPayload: SubjectMarksMapping = {
          ...(mappingFromModal || ({} as any)),
          className: selectedClassName || "",
          subjectCode: payload.subjectCode,
          subjectName: payload.name,
          subjectType: (mappingFromModal as any)?.subjectType || payload.subjectCategory || "",
          groupName: (mappingFromModal as any)?.groupName || "",
          totalMarks: Number((mappingFromModal as any)?.totalMarks ?? 100),
          theoryMarks: Number((mappingFromModal as any)?.theoryMarks ?? 80),
          practicalMarks: Number((mappingFromModal as any)?.practicalMarks ?? 20),
          thPassMarks: Number((mappingFromModal as any)?.thPassMarks ?? 0),
          prPassMarks: Number((mappingFromModal as any)?.prPassMarks ?? 0),
          isAllowedGrace: Boolean((mappingFromModal as any)?.isAllowedGrace ?? false),
          grace: Number((mappingFromModal as any)?.grace ?? 0),
          e1Th: Number((mappingFromModal as any)?.e1Th ?? 0),
          e1Pr: Number((mappingFromModal as any)?.e1Pr ?? 0),
          e2Th: Number((mappingFromModal as any)?.e2Th ?? 0),
          e2Pr: Number((mappingFromModal as any)?.e2Pr ?? 0),
        };

        const hasMappingId = Boolean((mappingPayload as any)?.id);
        await new Promise((resolve, reject) => {
          const runUpdate = () =>
            updateSubjectMarkMappingMutation.mutate(mappingPayload, {
              onSuccess: resolve,
              onError: reject,
            });

          if (hasMappingId) {
            runUpdate();
            return;
          }

          addSubjectMarkMappingMutation.mutate(mappingPayload, {
            onSuccess: resolve,
            onError: () => {
              // If mapping already exists, update via PUT
              runUpdate();
            },
          });
        });

        await new Promise((resolve, reject) => {
          createSubjectMutation.mutate(payload, {
            onSuccess: resolve,
            onError: reject,
          });
        });
      }

      showSuccess(
        t("subjects_bulk_created_successfully", { count: subjects.length })
      );
      handleCloseModal();
    } catch (error: any) {
      const backendMessage = error?.response?.data?.message;
      showError(backendMessage || t("error_creating_subjects"));
    } finally {
      setIsCreatingBulk(false);
    }
  }, [selectedClass, selectedClassName, schoolId, subjectsQuery.data, createSubjectMutation, handleCloseModal, showError, showSuccess, t]);

  // Helper function to check if a subject is used by any student in the SAME class
  const isSubjectUsedByStudents = useCallback((subjectCode: string, subjectClassId?: number, subjectClassName?: string): boolean => {
    if (!subjectCode) return false;

    // Normalize class name for comparison - extract base class name (e.g., "10th" from "10th-A")
    const normalizeClassName = (name?: string) => {
      if (!name) return '';
      // Remove spaces and convert to lowercase
      const cleaned = name.replace(/\s+/g, '').toLowerCase();
      // Extract base class name (before section separator like "-" or space)
      const baseMatch = cleaned.match(/^(\d+(?:th|st|nd|rd)?)/);
      return baseMatch ? baseMatch[1] : cleaned;
    };

    const normalizedSubjectBaseClass = normalizeClassName(subjectClassName);

    // Check if subject is used by any registered student in the SAME class
    const isUsedByStudent = studentsBySessionClassQuery.data?.some((student) => {
      // First check if student is in the same class by classId (most reliable)
      let isInSameClass = false;
      if (subjectClassId && student.schoolClassId) {
        isInSameClass = student.schoolClassId === subjectClassId;
      }

      // If classId match failed, check by className matching
      if (!isInSameClass && normalizedSubjectBaseClass && student.className) {
        const studentBaseClass = normalizeClassName(student.className);
        // Only match if base class names are exactly equal (e.g., "10th" === "10th", not "10th" === "9th")
        isInSameClass = studentBaseClass === normalizedSubjectBaseClass;
      }

      // If not in same class, skip this student
      if (!isInSameClass) return false;

      // Now check if this student uses the subject code
      if (!student.subjects) return false;

      // Handle both string and array formats for subjects
      let studentSubjectCodes: string[] = [];
      if (Array.isArray(student.subjects)) {
        studentSubjectCodes = student.subjects;
      } else if (typeof student.subjects === 'string') {
        studentSubjectCodes = student.subjects.split(',').map((s: string) => s.trim()).filter(Boolean);
      }

      // Check if the subject code is in the student's subjects
      return studentSubjectCodes.includes(subjectCode);
    }) || false;

    // Check if subject is used in any application form in the SAME class
    // Use queryClient.getQueryData to get the fetched data
    const applicationForms = queryClient.getQueryData<any[]>(["applicationFormsBySchoolId", schoolId]) || applicationFormsBySchoolIdQuery.data || [];
    const isUsedInApplicationForm = applicationForms.some((applicationForm) => {
      // Check if application form is in the same class
      let isInSameClass = false;

      // First check by classId (most reliable)
      if (subjectClassId && applicationForm.classId) {
        isInSameClass = applicationForm.classId === subjectClassId;
      }

      // If classId match failed, check by admissionClass matching
      if (!isInSameClass && normalizedSubjectBaseClass && applicationForm.admissionClass) {
        const applicationBaseClass = normalizeClassName(applicationForm.admissionClass);
        isInSameClass = applicationBaseClass === normalizedSubjectBaseClass;
      }

      // If not in same class, skip this application form
      if (!isInSameClass) return false;

      // Now check if this application form uses the subject code
      if (!applicationForm.subjects || !Array.isArray(applicationForm.subjects)) return false;

      // Check if the subject code is in the application form's subjects
      return applicationForm.subjects.includes(subjectCode);
    });

    return isUsedByStudent || isUsedInApplicationForm;
  }, [studentsBySessionClassQuery.data, applicationFormsBySchoolIdQuery.data, queryClient, schoolId]);

  const isSubjectInAnyMarksheet = useCallback(
    (subjectCode: string): boolean => {
      const code = String(subjectCode || "").trim();
      if (!code) return false;
      return marksheetSubjectCodes.has(code);
    },
    [marksheetSubjectCodes]
  );

  const handleOpenEditModal = useCallback(async (subject: Subject) => {
    const usedByStudentsOrApps = isSubjectUsedByStudents(subject.subjectCode, subject.classId, subject.className);
    const inAnyMarksheet = isSubjectInAnyMarksheet(subject.subjectCode);
    // If subject is present in any marksheet, don't allow editing anything.
    if (inAnyMarksheet) {
      showError(t("cannot_edit_subject_marksheet_exists") || "Cannot edit subject because marksheet exists.");
      return;
    }
    setIsEdit(true);
    setIsBulkMode(false);
    // If subject is used by any student/application, allow edit but lock core fields (name/code/etc.) and only allow mapping edits.
    setLockSubjectCoreFields(usedByStudentsOrApps);
    setLockSubjectAllFields(false);

    // Always use the subject directly from click
    setCurrentSubject({
      ...subject,
      subjectCategory: subject.subjectCategory || "",
      description: subject.description || "",
      active: subject.active ?? true,
    } as SubjectFormData);

    const getBaseClassName = (name?: string): string => {
      if (!name) return "";
      const match = name.match(/^(\d+(?:th|st|nd|rd)?)/i);
      return match ? match[1] : name.split(/\s+/)[0];
    };

    const baseClassName = getBaseClassName(subject.className || selectedClassName);
    if (baseClassName) {
      try {
        const mappings = await queryClient.fetchQuery({
          queryKey: ["subject-marks-mapping", baseClassName],
          queryFn: async () => {
            const response = await axios.get(
              `${process.env.REACT_APP_API_URL}/subject-mark-mapping/${baseClassName}`
            );
            return response.data as SubjectMarksMapping[];
          },
        });

        const matched = mappings?.find(
          (m) => String(m.subjectCode || "").trim() === String(subject.subjectCode || "").trim()
        );
        setCurrentMappingData(matched);
      } catch {
        setCurrentMappingData(undefined);
      }
    } else {
      setCurrentMappingData(undefined);
    }

    setModalOpen(true);
  }, [isSubjectUsedByStudents, isSubjectInAnyMarksheet, showError, t, queryClient, selectedClassName]);

  const handleOpenDeleteDialog = useCallback((id: number) => {

    // Find the subject to delete
    const subjectToDeleteData = subjectsQuery.data?.find((subject) => subject.id === id);
    if (!subjectToDeleteData) {
      showError(t("subject_not_found"));
      return;
    }

    // If subject is present in any marksheet, don't allow delete (as requested).
    if (isSubjectInAnyMarksheet(subjectToDeleteData.subjectCode)) {
      showError(
        t("cannot_delete_subject_marksheet_exists") ||
          "Cannot delete subject because marksheet exists."
      );
      return;
    }

    const usedByStudentsOrApps = isSubjectUsedByStudents(
      subjectToDeleteData.subjectCode,
      subjectToDeleteData.classId,
      subjectToDeleteData.className
    );
    const inAnyMarksheet = isSubjectInAnyMarksheet(subjectToDeleteData.subjectCode);
    // If used by students/applications, block delete.
    if (usedByStudentsOrApps && !inAnyMarksheet) {
      showError(
        `${t("cannot_delete_subject_used_by_students_or_applications")} "${subjectToDeleteData.name}" (${t("subject_code")}: ${subjectToDeleteData.subjectCode}). ${t("please_remove_subject_from_students_or_applications_before_deleting")}`
      );
      return;
    }

    // If subject is not used by students, allow deletion
    setSubjectToDelete(id);
    setDeleteDialogOpen(true);
  }, [studentsBySessionClassQuery.isLoading, studentsBySessionClassQuery.data, applicationFormsBySchoolIdQuery.isLoading, applicationFormsBySchoolIdQuery.data, subjectsQuery.data, isSubjectUsedByStudents, isSubjectInAnyMarksheet, showError, t]);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setSubjectToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (subjectToDelete !== null) {
      const subjectToDeleteData = subjectsQuery.data?.find((subject) => subject.id === subjectToDelete);

      if (!subjectToDeleteData) {
        showError(t("subject_not_found"));
        return;
      }

      try {
        await deleteSubjectMutation.mutateAsync(subjectToDelete);
        showSuccess(t("subject_deleted_successfully"));
        handleCloseDeleteDialog();
      } catch (error: unknown) {
        handleCloseDeleteDialog();

        const err = error as any;
        const backendMessage = String(err?.response?.data?.message || "");
        const shouldUseGenericMessage =
          backendMessage.toLowerCase().includes("violates foreign key constraint") ||
          backendMessage.toLowerCase().includes("could not execute statement") ||
          backendMessage.toLowerCase().includes("constraint") ||
          backendMessage.toLowerCase().includes("students_subjects");

        if (shouldUseGenericMessage) {
          showError(t("error_deleting_subject_generic"));
          return;
        }

        showError(backendMessage || t("error_deleting_subject"));
      }
    }
  }, [
    subjectToDelete,
    subjectsQuery.data,
    deleteSubjectMutation,
    handleCloseDeleteDialog,
    showError,
    showSuccess,
    t,
  ]);

  // 6. Memoize render actions function
  const renderActions = useCallback((row: any) => {
    const usedByStudentsOrApps = isSubjectUsedByStudents(row.subjectCode, row.classId, row.className);
    const inAnyMarksheet = isSubjectInAnyMarksheet(row.subjectCode);
    const editDisabled = inAnyMarksheet;
    const deleteDisabled = inAnyMarksheet || usedByStudentsOrApps;
    return (
      <ActionButtons
        onEdit={() => handleOpenEditModal(row)}
        onDelete={() => handleOpenDeleteDialog(row.id)}
        editTooltip={
          editDisabled
            ? (t("cannot_edit_subject_marksheet_exists") || t("cannot_delete_subject_used_by_students_or_applications_tooltip"))
            : usedByStudentsOrApps
              ? (t("only_marks_mapping_editable_subject_used") || t("edit"))
              : t("edit")
        }
        editDisabled={editDisabled}
        deleteTooltip={
          deleteDisabled
            ? (inAnyMarksheet
                ? (t("cannot_delete_subject_marksheet_exists") || t("cannot_delete_subject_used_by_students_or_applications_tooltip"))
                : t("cannot_delete_subject_used_by_students_or_applications_tooltip"))
            : t("delete")
        }
        deleteDisabled={deleteDisabled}
      />
    );
  }, [t, handleOpenEditModal, handleOpenDeleteDialog, isSubjectUsedByStudents, isSubjectInAnyMarksheet]);

  // 7. ALL useMemo hooks must come before any early returns
  // Optimize data filtering with useMemo - Use school-specific data only
  const classSubjects = useMemo(() => {
    if (!subjectsQuery.data || !schoolId) return [];

    // Filter by schoolId first
    const schoolSubjects = (subjectsQuery.data as Subject[])
      .filter(subject => subject.schoolId === schoolId);

    if (!selectedClass || !selectedClassName) {
      return [];
    }

    const normalizedSelectedClassIdString = selectedClass.name.replace(/\s+/g, '').toLowerCase();
    const normalizedSelectedSection = selectedClass.section?.replace(/\s+/g, '').toLowerCase();

    const classFilteredSubjects = schoolSubjects.filter((subject) => {
      if (subject.classId === selectedClass.id) {
        return true;
      }

      const normalizedSubjectClassName = subject.className?.replace(/\s+/g, '').toLowerCase();
      if (!normalizedSubjectClassName) {
        return false;
      }

      const baseMatch =
        normalizedSubjectClassName === normalizedSelectedClassIdString ||
        normalizedSubjectClassName.startsWith(normalizedSelectedClassIdString) ||
        normalizedSelectedClassIdString.startsWith(normalizedSubjectClassName);

      if (!baseMatch) {
        return false;
      }

      if (!normalizedSelectedSection) {
        return true;
      }

      const subjectHasSection = normalizedSubjectClassName.length > normalizedSelectedClassIdString.length;
      if (!subjectHasSection) {
        return true;
      }

      return normalizedSubjectClassName.includes(normalizedSelectedSection);
    });

    return classFilteredSubjects;
  }, [subjectsQuery.data, schoolId, selectedClass, selectedClassName]);

  // Memoize formatted data
  const formattedData = useMemo(() => {
    return classSubjects.map((row: Subject) => ({
      ...row,
      active: row.active,
      activeDisplay: row.active ? t("active") : t("inactive"),
    }));
  }, [classSubjects, t]);

  const filteredSubjectData = useMemo(() => {
    return formattedData.filter((row) => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase().trim();
      const isNumberSearch = /^\d+$/.test(searchLower);
      if (isNumberSearch) {
        const classNameValue = String(row.className || "").toLowerCase().trim();
        const classPattern = new RegExp(`^${searchLower}(st|nd|rd|th)`, 'i');
        if (classPattern.test(classNameValue)) {
          return true;
        }
      }
      return (
        String(row.name || "").toLowerCase().includes(searchLower) ||
        String(row.subjectCode || "").toLowerCase().includes(searchLower) ||
        String(row.description || "").toLowerCase().includes(searchLower) ||
        String(row.className || "").toLowerCase().includes(searchLower) ||
        String(row.subjectCategory || "").toLowerCase().includes(searchLower)
      );
    });
  }, [formattedData, searchTerm]);

  // Memoize existing subjects for the modal
  const existingSubjects = useMemo(() => {
    // Always return empty array if data is loading or not available
    if (subjectsQuery.isLoading || !subjectsQuery.data || !schoolId || !selectedClassName) {
      return [];
    }

    if (!selectedClass) {
      return [];
    }

    // Filter by schoolId first to get all subjects for this school
    const schoolSubjects = (subjectsQuery.data as Subject[]).filter(subject => subject.schoolId === schoolId);

    // For all other classes, return unique subjects for the selected class
    const normalizedSelectedClassName = selectedClass.name.replace(/\s+/g, '').toLowerCase();
    const normalizedSelectedSection = selectedClass.section?.replace(/\s+/g, '').toLowerCase();
    const filteredSubjects = schoolSubjects
      .filter(subject => {
        if (subject.classId === selectedClass.id) {
          return true;
        }
        const normalizedSubjectClassName = subject.className
          ?.replace(/\s+/g, '')
          .toLowerCase();
        if (!normalizedSubjectClassName) {
          return false;
        }

        const baseMatch =
          normalizedSubjectClassName === normalizedSelectedClassName ||
          normalizedSubjectClassName.startsWith(normalizedSelectedClassName) ||
          normalizedSelectedClassName.startsWith(normalizedSubjectClassName);

        if (!baseMatch) {
          return false;
        }

        if (!normalizedSelectedSection) {
          return true;
        }

        const subjectHasSection = normalizedSubjectClassName.length > normalizedSelectedClassName.length;
        if (!subjectHasSection) {
          return true;
        }

        return normalizedSubjectClassName.includes(normalizedSelectedSection);
      })
      .map(subject => {
        // Return subject codes for checking (skip subjects that already exist by code)
        return subject.subjectCode || '';
      })
      .filter(code => code !== ''); // Remove empty codes

    return filteredSubjects.filter((code, index) => filteredSubjects.indexOf(code) === index);
  }, [subjectsQuery.isLoading, subjectsQuery.isError, subjectsQuery.data, subjectsQuery.isSuccess, classSubjects, selectedClass, selectedClassName, schoolId]);

  // Memoize columns
  const columns = useMemo(() => [
    { id: "name", label: t("subject_name"), minWidth: 130 },
    { id: "subjectCode", label: t("subject_code"), minWidth: 100 },
    // { id: "description", label: t("subject_description"), minWidth: 200 },
    { id: "className", label: t("subject_class"), minWidth: 100 },
    { id: "activeDisplay", label: t("subject_status"), minWidth: 100 },
    { id: "subjectCategory", label: t("subject_category"), minWidth: 130 },
  ], [t]);

  // 8. Handle loading state with better UX
  // For school admin and teachers, don't wait for schoolIdLoading since they already have schoolId from user context
  const isWaitingForSchoolId = !isSchoolAdmin() && !isTeacher() && (schoolIdLoading || !schoolId);
  if (isWaitingForSchoolId) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>
          {schoolIdLoading
            ? t("loading_school_information")
            : t("unable_to_determine_school")
          }
        </Typography>
      </Box>
    );
  }

  // If school admin but no schoolId, show error instead of infinite loading
  if (isSchoolAdmin() && !schoolId) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <Alert severity="warning" sx={{ maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>
            {t("school_configuration_issue")}
          </Typography>
          <Typography variant="body2">
            {t("account_not_properly_configured")}
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (subjectsQuery.isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>{t("loading_subjects")}</Typography>
      </Box>
    );
  }

  // 9. Handle error state
  if (subjectsQuery.error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <Alert severity="error">
          {t("failed_to_load_subjects")}
        </Alert>
      </Box>
    );
  }

  // 10. Handle school ID validation
  if (!schoolId) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <Alert severity="warning" sx={{ maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>
            {t("school_configuration_issue")}
          </Typography>
          <Typography variant="body2" paragraph>
            {t("account_not_properly_configured")}
          </Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 2 }}>
            <li>{t("account_recently_created")}</li>
            <li>{t("data_inconsistency")}</li>
            <li>{t("school_deleted_deactivated")}</li>
          </Typography>
          <Typography variant="body2">
            {t("contact_system_administrator")}
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <Paper sx={{ p: 1.5, maxWidth: 1500, mx: "auto", mt: 1 }}>
        {(!selectedSession || !selectedClass) && (
          <Alert severity="warning" sx={{ mb: 1, py: 0.5, px: 1.5, backgroundColor: "#fff3cd", color: "#856404" }}>
            {t("please_select_session_and_class_before_creating_subjects")}
          </Alert>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              fontSize: "1.25rem",
              whiteSpace: "nowrap",
            }}
          >
            {t("subject_management")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <TextField
              size="small"
              placeholder={t("search")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "rgba(0,0,0,0.6)" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 180, '& .MuiInputBase-root': { height: 36 } }}
            />
            <Tooltip
              title={selectedSession && !selectedSession.isActive ? t("session_is_inactive") || "Session is inactive" : ""}
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  size="small"
                  sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, height: 36 }}
                  onClick={handleOpenBulkModal}
                  disabled={!selectedSession || !selectedClass || (selectedSession && !selectedSession.isActive)}
                >
                  {t("add_multiple_subjects")}
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={selectedSession && !selectedSession.isActive ? t("session_is_inactive") || "Session is inactive" : ""}
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  size="small"
                  sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, height: 36 }}
                  onClick={handleOpenAddModal}
                  disabled={!selectedSession || !selectedClass || (selectedSession && !selectedSession.isActive)}
                >
                  {t("add_subject")}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        <EnhancedTable
          columns={columns}
          data={filteredSubjectData}
          searchFields={[]}
          filterFields={[]}
          cardProps={{
            elevation: 0,
            sx: {
              border: 'none',
              boxShadow: 'none',
              backgroundColor: 'transparent',
              mb: 0,
              '& .MuiCardContent-root': {
                p: 0,
                '&:last-child': { pb: 0 }
              }
            }
          }}
          actions={renderActions}
        />
      </Paper>

      <SubjectFormModalAny
        key={currentSubject?.id || (isBulkMode ? "bulk" : "new")}
        open={modalOpen}
        handleClose={handleCloseModal}
        initialData={currentSubject}
        initialMappingData={currentMappingData}
        onSubmit={handleSubmit}
        onSubmitBulk={handleBulkSubmit}
        isEdit={isEdit}
        isBulkMode={isBulkMode}
        lockSubjectCoreFields={lockSubjectCoreFields}
        lockAllFields={lockSubjectAllFields}
        existingSubjects={existingSubjects}
        isLoading={subjectsQuery.isLoading}
        isSubmitting={createSubjectMutation.isLoading || updateSubjectMutation.isLoading || addSubjectMarkMappingMutation.isLoading || isCreatingBulk}
      />

      {/* Delete Confirmation Dialog */}
      <CommonModal
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={t("confirm_delete")}
        maxWidth="sm"
        onSubmit={handleConfirmDelete}
        submitLabel={t("delete")}
        danger
      >
        <Typography variant="body1" color="text.secondary">
          {t("are_you_sure_you_want_to_delete_this_subject")}
        </Typography>
      </CommonModal>
    </>
  );
};

export default SubjectManagement;